import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  Chip,
} from '@mui/material'
import { DataGridTable, TypoGraph } from 'components'
import { reportException } from 'tools/logs'
import moment, { Moment } from 'moment'
import { formatDateMMDDYYYY, formatDateYYYYMMDD } from 'tools/helper'
import { ActiveVehicles } from 'interfaces/ISummaryReport'
import styles from './activeUnits.module.scss'
import strings from 'strings'
import { getActiveVehicles } from 'api'
import { getActiveUnitsColumns } from './activeUnitsTableData'

const r = strings.SUMMARY_REPORT
const t = strings.SUMMARY_REPORT.ACTIVE_UNITS
const s = strings.TABLE_DEFAULTS

interface ActiveUnitsProps {
  onPdfDataChange?: (data: any[][] | null, columns: string[] | null) => void
  onLoadingChange?: (isLoading: boolean) => void
}

const ActiveUnits: React.FC<ActiveUnitsProps> = ({ onPdfDataChange, onLoadingChange }) => {
  const carrierId = localStorage.getItem('carrierId') || '1'
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [activeVehiclesData, setActiveVehiclesData] =
    useState<ActiveVehicles | null>(null)
  const [hasActiveVehicles, setHasActiveVehicles] = useState<boolean>(false)
  const [loadingActiveVehicles, setLoadingActiveVehicles] =
    useState<boolean>(false)

  const pdfColumns = useMemo(() => {
    return [
      t.VIN,
      t.YEAR,
      t.MAKE,
      t.GARAGING_ZIPCODE,
      t.TOTAL_MILES,
      t.ACTIVE_DAYS,
      t.AVG_MILES_DAY,
      t.SAFETY_EVENTS,
      t.TIME_SPEEDING,
      t.CURRENT_RISK_SCORE,
      t.LAST_MONTH_SCORE,
      t.SCORE_CHANGE,
    ]
  }, [])

  const pdfData = useMemo(() => {
    if (!activeVehiclesData?.vehicles || activeVehiclesData.vehicles.length === 0) {
      return null
    }

    return activeVehiclesData.vehicles.map((vehicle) => {
      return [
        vehicle.vin || s.DASH_VALUE,
        vehicle.year || s.DASH_VALUE,
        vehicle.make || s.DASH_VALUE,
        vehicle.garagingZipCode != null ? vehicle.garagingZipCode : s.DASH_VALUE,
        vehicle.totalMiles ? Math.round(vehicle.totalMiles).toLocaleString() : s.DASH_VALUE,
        vehicle.totalActiveDays ? Math.round(vehicle.totalActiveDays).toLocaleString() : s.DASH_VALUE,
        vehicle.avgMilesDays ? Math.round(vehicle.avgMilesDays).toLocaleString() : s.DASH_VALUE,
        vehicle.safetyEventsCount != null 
          ? vehicle.safetyEventsCount.toString() 
          : t.EMPTY_VALUE,
        vehicle.timeSpeedingPercentage != null 
          ? `${vehicle.timeSpeedingPercentage.toFixed(2)}%` 
          : s.NA,
        vehicle.latestScore != null ? vehicle.latestScore.toFixed(2) : s.NA,
        vehicle.previousScore != null ? vehicle.previousScore.toFixed(2) : s.NA,
        vehicle.previousScore === null || !vehicle.latestScore
          ? s.NA
          : `${vehicle.scoreDifference > 0 ? '+' : ''}${vehicle.scoreDifference.toFixed(2)}`,
      ]
    })
  }, [activeVehiclesData])

  useEffect(() => {
    onPdfDataChange?.(pdfData, pdfData ? pdfColumns : null)
  }, [pdfData, pdfColumns, onPdfDataChange])

  useEffect(() => {
    onLoadingChange?.(loadingActiveVehicles)
  }, [loadingActiveVehicles, onLoadingChange])

  useEffect(() => {
    const currentDate = moment().utc()
    const startDate = moment().utc().subtract(90, 'days')
    setStartDate(startDate)
    setEndDate(currentDate)
  }, [])

  useEffect(() => {
    if (startDate && endDate) {
      callGetActiveVehicles()
    }
  }, [startDate, endDate])

  const callGetActiveVehicles = async () => {
    if (!startDate || !endDate) return

    setLoadingActiveVehicles(true)
    try {
      const startDateFormatted = formatDateYYYYMMDD(startDate)
      const endDateFormatted = formatDateYYYYMMDD(endDate)

      const res = await getActiveVehicles(
        carrierId,
        startDateFormatted,
        endDateFormatted
      )

      if (res && res.vehicles && res.vehicles.length > 0) {
        setActiveVehiclesData(res)
        setHasActiveVehicles(true)
      } else {
        setActiveVehiclesData(null)
        setHasActiveVehicles(false)
      }
    } catch (error) {
      reportException(error)
      setHasActiveVehicles(false)
      setActiveVehiclesData(null)
    } finally {
      setLoadingActiveVehicles(false)
    }
  }

  const createPerformanceChip = (value: number | string, isPoor: boolean) => {
    const isEmpty = value === undefined || value === null || value === ''
    const displayValue = isEmpty ? t.EMPTY_VALUE : value

    const chipClass = !isEmpty
      ? isPoor
        ? styles.chipPoor
        : styles.chipGood
      : styles.chipEmpty

    return (
        <Chip
          label={displayValue}
          size="small"
          className={`${styles.performanceChip} ${chipClass}`}
        />
    )
  }
  const calculatedOnText = `${
    r.CALCULATED_ON +
    formatDateMMDDYYYY(moment().subtract(1, 'days').toISOString())
  } 
      ${r.DAYS_OF_DATA_PRE} ${
    endDate && startDate ? moment(endDate).diff(moment(startDate), 'days') : 0
  } 
          ${r.DAYS_OF_DATA_POST}`

  const ColorCoding = () => {
    return (
      <>
        <Box
          className={styles.colorCodingContainer}
        >
          <Box className={styles.colorCodingItem}>
            <Typography variant="caption" mr={2} color="text.secondary">
              {t.COLOR_CODING_TITLE}
            </Typography>

            <Box className={styles.colorCodingItem}>
              <Box className={`${styles.colorDot} ${styles.redDot}`} />
              <Typography variant="caption" color="text.secondary">
                {t.RED_LEGEND}
              </Typography>
            </Box>

            <Box className={styles.colorCodingItem}>
              <Box className={`${styles.colorDot} ${styles.grayDot}`} />
              <Typography variant="caption" color="text.secondary">
                {t.GRAY_LEGEND}
              </Typography>
            </Box>
          </Box>
        </Box>
      </>
    )
  }

  return (
    <Box className={styles.pageContainer}>
      <TypoGraph
        variant="h2"
        className={styles.title}
        mb={0}
        content={t.ACTIVE_UNITS_TITLE}
      />
      <TypoGraph
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1 }}
      >
        {calculatedOnText}
      </TypoGraph>
      <ColorCoding />
      <Grid>
        {loadingActiveVehicles ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={30} />
          </Box>
        ) : hasActiveVehicles && activeVehiclesData?.vehicles ? (
          <div className={styles.tableContainer}>
            <DataGridTable
              showTitle={false}
              showFilter={false}
              reduceHeight={250}
              id="activeVehiclesTable"
              loading={loadingActiveVehicles}
              data={activeVehiclesData.vehicles}
              columns={getActiveUnitsColumns(t, createPerformanceChip)}
              totalCount={activeVehiclesData.summary.totalVehicles}
              checkboxSelection={false}
              multiSearchFilter={true}
              getRowId={(row: any) => row.rowId}
            />
          </div>
        ) : (
          <Box className={styles.noDataContainer}>
            <Typography variant="body1" color="text.secondary">
              {t.NO_DATA}
            </Typography>
          </Box>
        )}
      </Grid>
    </Box>
  )
}

export default ActiveUnits
